import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { usePetitionAiGuidanceExperiment } from 'src/app/pages/starterDashboardV2/shared/hooks';
import { useSessionStorageValue } from 'src/app/shared/hooks/storage';

import { TASKS_CONFIGS } from '../../../../constants';
import { PERSONALIZED_TASK_STATUSES, PERSONALIZED_TASKS_GENERATION_STATUSES } from '../../constants';
import type { UseDynamicTasksReturn } from '../../types';
import { useDynamicCheckboxAction } from '../useDynamicCheckboxAction';
import { useDynamicTaskAction } from '../useDynamicTaskAction';
import { usePollPersonalizedTasks } from '../usePollPersonalizedTasks';

import { DEFAULT_TASKS_DISPLAY_COUNT, DYNAMIC_TASKS_EXPANDED_STORAGE_KEY, getToggleTaskListData } from './model';
import {
	buildTaskShapes,
	getDisplayedTasks,
	getTaskStats,
	orderDynamicTasksForDisplay,
	parsePersonalizedTasks,
} from './taskShapes';
import { type PendingCtaSync, usePendingPersonalizedTaskSync } from './usePendingPersonalizedTaskSync';

const AI_GUIDANCE_PROMPT_FAILS_EVENT = 'ai_guidance_prompt_fails';
const AI_GUIDANCE_PROMPT_SUCCEEDS_EVENT = 'ai_guidance_prompt_succeeds';
const AI_GUIDANCE_VERSION = 'new_ai_03_2026';

function shouldPollPersonalizedTasks({
	generationStatus,
	isEnabled,
}: {
	generationStatus: string;
	isEnabled: boolean;
}): boolean {
	return (
		isEnabled &&
		(generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.PENDING ||
			generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.IN_PROGRESS)
	);
}

function useResolvedPersonalizedTasks({
	petition,
	isEnabled,
}: {
	petition: StarterDashboardV2PageData['petition'];
	isEnabled: boolean;
}) {
	const initialResult = parsePersonalizedTasks(petition.personalizedTasks);
	const polledData = usePollPersonalizedTasks({
		slug: petition.slug,
		shouldPoll: shouldPollPersonalizedTasks({
			generationStatus: initialResult.generationStatus,
			isEnabled,
		}),
	});

	return useMemo(
		() => (polledData ? parsePersonalizedTasks(polledData) : parsePersonalizedTasks(petition.personalizedTasks)),
		[polledData, petition.personalizedTasks],
	);
}

function useTrackAiGuidanceGenerationStatus({
	generationStatus,
	isEnabled,
}: {
	generationStatus: string;
	isEnabled: boolean;
}): void {
	const track = useTracking();
	const trackedGenerationStatusRef = useRef<string | null>(null);

	useEffect(() => {
		if (!isEnabled || trackedGenerationStatusRef.current === generationStatus) {
			return;
		}

		if (generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.COMPLETED) {
			trackedGenerationStatusRef.current = generationStatus;
			void track(AI_GUIDANCE_PROMPT_SUCCEEDS_EVENT, { version: AI_GUIDANCE_VERSION });
		}

		if (generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.FAILED) {
			trackedGenerationStatusRef.current = generationStatus;
			void track(AI_GUIDANCE_PROMPT_FAILS_EVENT, { version: AI_GUIDANCE_VERSION });
		}
	}, [generationStatus, isEnabled, track]);
}

/**
 * Hook that provides dynamic (AI-generated) tasks data.
 */
export function useDynamicTasks(): UseDynamicTasksReturn {
	const {
		data: { isEnabled: isPetitionAiGuidanceEnabled },
	} = usePetitionAiGuidanceExperiment();
	const {
		data,
		actions: { refreshData },
	} = useStarterDashboardV2PageData();
	const pendingCtaSyncRef = useRef<PendingCtaSync | null>(null);
	const clearPendingCtaSync = useCallback(() => {
		pendingCtaSyncRef.current = null;
	}, []);
	usePendingPersonalizedTaskSync(data.petition, pendingCtaSyncRef, clearPendingCtaSync, refreshData);

	const parsedTasks = useResolvedPersonalizedTasks({
		petition: data.petition,
		isEnabled: isPetitionAiGuidanceEnabled,
	});

	const { generationStatus, tasks } = parsedTasks;
	const shouldShowFallbackNextSteps = generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.FAILED;

	const [optimisticCompletedTaskIds, setOptimisticCompletedTaskIds] = useState<ReadonlySet<string>>(() => new Set());

	useTrackAiGuidanceGenerationStatus({
		generationStatus,
		isEnabled: isPetitionAiGuidanceEnabled,
	});

	const getCtaAction = useDynamicTaskAction();
	const getCheckboxAction = useDynamicCheckboxAction();
	const allTasks = useMemo(() => {
		const baseShapes = buildTaskShapes(tasks);
		const mapped = baseShapes.map((task) => {
			const uiCompleted =
				task.status === PERSONALIZED_TASK_STATUSES.COMPLETED || optimisticCompletedTaskIds.has(task.id);
			const rawCta = getCtaAction(task.featureKey, task.id, task.status);
			const runCheckboxAction = getCheckboxAction(task.featureKey, task.id, task.title);
			return {
				...task,
				uiCompleted,
				onCtaClick: rawCta
					? async () => {
							const completed = await rawCta();
							const taskFeatureKey = task.featureKey;
							if (taskFeatureKey && Object.hasOwn(TASKS_CONFIGS, taskFeatureKey)) {
								pendingCtaSyncRef.current = {
									personalizedTaskId: task.id,
									featureKey: taskFeatureKey,
								};
							}
							refreshData();
							return completed;
						}
					: undefined,
				onCheckboxChange: async () => {
					setOptimisticCompletedTaskIds((previousIds) => {
						if (previousIds.has(task.id)) {
							return previousIds;
						}
						return new Set([...previousIds, task.id]);
					});
					try {
						await runCheckboxAction();
					} catch {
						setOptimisticCompletedTaskIds((previousIds) => {
							if (!previousIds.has(task.id)) {
								return previousIds;
							}
							const updatedIds = new Set(previousIds);
							updatedIds.delete(task.id);
							return updatedIds;
						});
					}
				},
			};
		});

		return orderDynamicTasksForDisplay(mapped);
	}, [tasks, optimisticCompletedTaskIds, getCtaAction, getCheckboxAction, refreshData]);

	const [expandedStorageValue, setExpandedStorageValue] = useSessionStorageValue(DYNAMIC_TASKS_EXPANDED_STORAGE_KEY);
	const taskListIsExpanded = expandedStorageValue === 'true';
	const displayCount = taskListIsExpanded ? allTasks.length : DEFAULT_TASKS_DISPLAY_COUNT;

	const { nextExpandedStorageValue, ...toggleData } = getToggleTaskListData(
		allTasks,
		taskListIsExpanded,
		generationStatus,
	);
	const displayedTasks = getDisplayedTasks(allTasks, displayCount);
	const stats = getTaskStats(allTasks);

	return {
		data: {
			tasks: displayedTasks,
			stats,
			generationStatus,
			shouldShowFallbackNextSteps,
			taskListIsExpanded,
			...toggleData,
		},
		actions: {
			toggleTaskList: () => setExpandedStorageValue(nextExpandedStorageValue),
		},
	};
}
